import { useCallback, useEffect, useState } from 'react';
import * as Types from 'types/types';
import { SeverityType, SetSnackType } from 'components/Snack';
import { useAuth } from 'hooks/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppStateContextType, RouteState } from './appStateTypes';
import {
  clearStoredRouteState,
  getInitialRouteState,
  getStoredId,
  persistRouteState,
  setStoredId,
  STORAGE_KEYS,
} from './appStateStorage';

export function useAppStateLogic(): AppStateContextType {
  const location = useLocation();
  const navigate = useNavigate();
  const { apiClient } = useAuth();
  const [selectedCandidate, setSelectedCandidateState] = useState<Types.Candidate | null>(null);
  const [selectedJob, setSelectedJobState] = useState<Types.Job | null>(null);
  const [selectedEmployer, setSelectedEmployerState] = useState<Types.Employer | null>(null);
  const [selectedResume, setSelectedResume] = useState<Types.Resume | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [routeState, setRouteStateState] = useState<RouteState>(getInitialRouteState);

  useEffect(() => {
    const initializeFromStorage = async (): Promise<void> => {
      setIsInitializing(true);

      try {
        await Promise.all([
          restoreCandidate(
            getStoredId(STORAGE_KEYS.SELECTED_CANDIDATE_ID),
            apiClient.getCandidate,
            setSelectedCandidateState
          ),
          restoreJob(
            getStoredId(STORAGE_KEYS.SELECTED_JOB_ID),
            apiClient.getJob,
            setSelectedJobState
          ),
          restoreEmployer(
            getStoredId(STORAGE_KEYS.SELECTED_EMPLOYER_ID),
            apiClient.getEmployer,
            setSelectedEmployerState
          ),
        ]);
      } catch (error) {
        console.error('Error during app state initialization:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeFromStorage();
  }, [apiClient]);

  useEffect(() => {
    const excludedRoutes = ['/login', '/register', '/verify-email', '/reset-password'];
    const shouldSaveRoute = !excludedRoutes.some(route => location.pathname.startsWith(route));

    if (shouldSaveRoute && !isInitializing) {
      setRouteStateState(prev => {
        const newState = { ...prev, lastRoute: location.pathname };
        persistRouteState(newState);
        return newState;
      });
    }
  }, [location.pathname, isInitializing]);

  const setSelectedCandidate = useCallback((candidate: Types.Candidate | null) => {
    setSelectedCandidateState(candidate);
    setStoredId(STORAGE_KEYS.SELECTED_CANDIDATE_ID, candidate?.id || null);
  }, []);

  const setSelectedJob = useCallback((job: Types.Job | null) => {
    setSelectedJobState(job);
    setStoredId(STORAGE_KEYS.SELECTED_JOB_ID, job?.id || null);
  }, []);

  const setSelectedEmployer = useCallback((employer: Types.Employer | null) => {
    setSelectedEmployerState(employer);
    setStoredId(STORAGE_KEYS.SELECTED_EMPLOYER_ID, employer?.id || null);
  }, []);

  const clearSelections = useCallback(() => {
    setSelectedCandidateState(null);
    setSelectedJobState(null);
    setSelectedEmployerState(null);
    setStoredId(STORAGE_KEYS.SELECTED_CANDIDATE_ID, null);
    setStoredId(STORAGE_KEYS.SELECTED_JOB_ID, null);
    setStoredId(STORAGE_KEYS.SELECTED_EMPLOYER_ID, null);
  }, []);

  const saveCurrentRoute = useCallback(() => {
    setRouteStateState(prev => {
      const newState = { ...prev, lastRoute: location.pathname };
      persistRouteState(newState);
      return newState;
    });
  }, [location.pathname]);

  const restoreLastRoute = useCallback(() => {
    if (routeState.lastRoute && routeState.lastRoute !== location.pathname) {
      navigate(routeState.lastRoute);
    }
  }, [routeState.lastRoute, location.pathname, navigate]);

  const setActiveTab = useCallback((tab: string) => {
    updateRouteState(setRouteStateState, { activeTab: tab });
  }, []);

  const setFilters = useCallback((filters: Record<string, unknown>) => {
    updateRouteState(setRouteStateState, { appliedFilters: filters });
  }, []);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    updateRouteState(setRouteStateState, { sidebarCollapsed: collapsed });
  }, []);

  const clearRouteState = useCallback(() => {
    const clearedState = {
      lastRoute: null,
      activeTab: null,
      appliedFilters: {},
      sidebarCollapsed: false,
    };
    setRouteStateState(clearedState);
    clearStoredRouteState();
  }, []);

  const emptySetSnack: SetSnackType = (_message: string, _severity?: SeverityType) => {
    return;
  };

  return {
    setSnack: emptySetSnack,
    selectedCandidate,
    selectedJob,
    selectedEmployer,
    routeState,
    selectedResume,
    setSelectedResume,
    isInitializing,
    setSelectedCandidate,
    setSelectedJob,
    setSelectedEmployer,
    clearSelections,
    saveCurrentRoute,
    restoreLastRoute,
    setActiveTab,
    setFilters,
    setSidebarCollapsed,
    clearRouteState,
  };
}

function updateRouteState(
  setRouteState: React.Dispatch<React.SetStateAction<RouteState>>,
  update: Partial<RouteState>
): void {
  setRouteState(prev => {
    const newState = { ...prev, ...update };
    persistRouteState(newState);
    return newState;
  });
}

async function restoreCandidate(
  id: string | null,
  loader: (id: string) => Promise<Types.Candidate>,
  setSelected: (candidate: Types.Candidate | null) => void
): Promise<void> {
  if (!id) return;
  await restoreEntity(id, STORAGE_KEYS.SELECTED_CANDIDATE_ID, loader, setSelected);
}

async function restoreJob(
  id: string | null,
  loader: (id: string) => Promise<Types.Job>,
  setSelected: (job: Types.Job | null) => void
): Promise<void> {
  if (!id) return;
  await restoreEntity(id, STORAGE_KEYS.SELECTED_JOB_ID, loader, setSelected);
}

async function restoreEmployer(
  id: string | null,
  loader: (id: string) => Promise<Types.Employer>,
  setSelected: (employer: Types.Employer | null) => void
): Promise<void> {
  if (!id) return;
  await restoreEntity(id, STORAGE_KEYS.SELECTED_EMPLOYER_ID, loader, setSelected);
}

async function restoreEntity<T>(
  id: string,
  storageKey: string,
  loader: (id: string) => Promise<T>,
  setSelected: (entity: T | null) => void
): Promise<void> {
  try {
    const entity = await loader(id);
    if (entity) {
      setSelected(entity);
      return;
    }
  } catch (error) {
    console.warn(`Failed to restore ${storageKey}:`, error);
  }

  setStoredId(storageKey, null);
}
